import { Image, Tooltip } from "antd";
import cls from "classnames";
import { observer } from "mobx-react";
import { EyeOutlined } from "@ant-design/icons";
import { EUploadStatus } from "logic/src/objects/file/types";
import {
  EFileQueryType,
  ESendMsgType,
  IChatSendMessage,
  IChatTextMessage,
} from "logic/src/objects/chat/types";
import { getFileType } from "logic/src/objects/file/utils";
import FileDisplay from "@/components/FileDisplay";
import LinkDialog from "logic/src/components/LinkDialog";
import { getLinkFilePreviewTip } from "logic/src/objects/session/sessionFile";
import { ESessionFileType, ISessionFile } from "logic/src/objects/session/type";
import { ISelection } from "logic/src/objects/chat/types";
import RefText from "logic/src/components/RefText";

import styles from "./index.module.scss";

interface IProps {
  isLogin: boolean;
  isTransMsg?: boolean;
  files?: ISessionFile[];
  sessionFileIds: string[];
  sessionLinkFileIdMap: Record<string, ISessionFile>;
  messageList: IChatSendMessage[];
  textMessageList: IChatTextMessage[];
  onPreviewFile: (file_id: string) => void;
  fileSelection?: ISelection;
  onFileSelectionClick?: (v: ISelection, fileId: string) => void;
}

const PopUser: React.FC<IProps> = ({
  isLogin,
  files = [],
  isTransMsg,
  sessionFileIds,
  messageList,
  textMessageList,
  sessionLinkFileIdMap,
  onPreviewFile,
  onFileSelectionClick,
}) => {
  const hasFiles = !!files && !!files.length;
  const fileList =
    files?.filter(item => item.type === ESessionFileType.file) || [];
  const imgList =
    files?.filter(item => item.type === ESessionFileType.image) || [];

  // 用户输入问题
  const fileMsg = messageList.find(
    ele => ele.msg_type === ESendMsgType.file_query,
  );

  let fileSelection: ISelection | undefined;

  if (fileMsg && fileMsg.file_query?.location) {
    try {
      const res = JSON.parse(fileMsg.file_query?.location);
      fileSelection = {
        ...res,
        text: fileMsg.file_query.file_query_msg,
      };
    } catch (e) {
      console.error(fileMsg.file_query?.location);
    }
  }

  const handleFilePreview = (file_id?: string) => {
    if (!isLogin || !file_id || !sessionFileIds.includes(file_id)) return;
    onPreviewFile(file_id);
  };

  // 上传文件展示内容
  const genFileList = (list: ISessionFile[]) => {
    if (!list || !list.length) return null;
    return (
      <div className={styles["file-list"]}>
        {fileList.map((item, index) => (
          <span
            key={index}
            className={styles["file-item"]}
            onClick={() => handleFilePreview(item.file_id)}
          >
            {!isLogin ||
            !item.file_id ||
            sessionFileIds.includes(item.file_id) ? (
              <FileDisplay
                fileInfo={{
                  file_name: item.file_name,
                  url: item.url,
                  status: "",
                }}
                className={styles.fileDisplayCls}
                uploadStatus={EUploadStatus.completed}
                statusText={getFileType(item.file_name)?.toUpperCase()}
              />
            ) : (
              <Tooltip
                placement="bottom"
                title="已删除，商量回答时不会继续参考"
                overlayStyle={{ maxWidth: "max-content" }}
                overlayInnerStyle={{ minHeight: "auto" }}
              >
                <span>
                  <FileDisplay
                    fileInfo={{
                      file_name: item.file_name,
                      url: item.url,
                      status: "",
                    }}
                    className={styles.fileDisplayDel}
                    uploadStatus={EUploadStatus.completed}
                    statusText={getFileType(item.file_name)?.toUpperCase()}
                  />
                </span>
              </Tooltip>
            )}
          </span>
        ))}
      </div>
    );
  };

  // 上传图片展示内容
  const genImgList = (imgFileList: ISessionFile[]) => {
    if (!imgFileList || !imgFileList.length) return null;
    if (imgFileList.length === 1) {
      const item = imgFileList[0];
      return (
        <div className={styles["file-list"]}>
          <Image
            key={item.url}
            src={item.url}
            // preview={false}
            className={styles["img-single"]}
            // onClick={() => handleFilePreview(item.file_id)}
            preview={{
              mask: (
                <div>
                  <EyeOutlined />
                </div>
              ),
            }}
          />
        </div>
      );
    }

    return (
      <div className={styles["file-list"]}>
        {imgFileList.map((item, index) => (
          <span
            key={index}
            className={styles["file-item"]}
            // onClick={() => handleFilePreview(item.file_id)}
          >
            <FileDisplay
              canPreview
              fileInfo={{
                file_name: item.file_name,
                url: item.url,
                status: "",
              }}
              uploadStatus={EUploadStatus.completed}
            />
          </span>
        ))}
      </div>
    );
  };

  // url & 文本展示内容
  const genTextAndUrl = () => {
    if (!textMessageList) return null;
    const resList: any[] = [];
    textMessageList.forEach(({ type, content, fileId }) => {
      if (type === "url") {
        const file = sessionLinkFileIdMap[fileId!];
        let errorTip = "";
        let statusCls = "";
        let popoverProps = {};

        if (!file) {
          statusCls = "error";
          errorTip = "已删除，商量回答时不会继续参考";
        } else {
          const { status, tip } = getLinkFilePreviewTip(
            file.status,
            file.contentParseStatus,
            file?.contentParseRes || {},
          );
          statusCls = status;
          errorTip = tip;
          !tip && (popoverProps = file?.contentParseRes || {});
        }

        const canLinkHover = statusCls === "success" && !errorTip;

        resList.push(
          <div>
            <span
              className={styles.link}
              // onClick={() => handleFilePreview(file?.file_id)}
              onClick={() => window.open(file?.file_name, "_blank")}
            >
              {!canLinkHover && content}
              <LinkDialog
                type={canLinkHover ? "popover" : "tooltip"}
                placement={canLinkHover ? "rightTop" : "top"}
                tooltipProps={{ content: errorTip }}
                popoverProps={{
                  ...popoverProps,
                  // onClick: () => handleFilePreview(file?.file_id),
                  onClick: () => window.open(file?.file_name, "_blank"),
                }}
              >
                <>
                  {canLinkHover && content}
                  <span className={cls(styles.status, styles[statusCls])} />
                </>
              </LinkDialog>
            </span>
          </div>,
        );
      } else {
        resList.push(content);
      }
    });
    return resList;
  };

  const userMsg = !isTransMsg
    ? genTextAndUrl()
    : messageList.find(msg => msg.msg_type === ESendMsgType.trans_query)
        ?.trans_query;

  return (
    <div className={styles["chat-pop-user"]}>
      {hasFiles && (
        <div className={styles["file-wrap"]}>
          {genFileList(fileList)}
          {genImgList(imgList)}
        </div>
      )}

      {/* 展示url文件与普通文本 或 翻译prompt */}
      <div className={styles["question-text"]}>{userMsg}</div>

      {isTransMsg && (
        <div className={styles.transContent}>
          {
            messageList.find(msg => msg.msg_type === ESendMsgType.user_query)
              ?.user_query
          }
        </div>
      )}

      {fileSelection && (
        <div className={styles.selection}>
          <RefText
            text={fileSelection.text}
            important={
              fileMsg?.file_query?.file_query_type === EFileQueryType.key_point
            }
            onClick={() =>
              onFileSelectionClick?.(
                { ...fileSelection! },
                fileMsg!.file_query!.file_id,
              )
            }
          />
        </div>
      )}
    </div>
  );
};

export default observer(PopUser);
